import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import TableLoader from "components/common/TableLoader/TableLoader";
import React, {
    ChangeEvent,
    useContext,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { getFiUser, sumLineAmounts } from "../../function";
import { dateFormatter } from "./function";

interface Column {
    name: string;
    type: "string" | "number" | "date" | "email" | "boolean" | "object";
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    format?: string;
    required?: boolean;
    fields?: Field[];
}

interface Field {
    name: string;
    type: "string" | "number";
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    required?: boolean;
}

interface Schema {
    columns: Column[];
    rowData: any[][];
}

const MtDynamicTableLabel = React.forwardRef(
    (
        {
            schema,
            onSubmit,
            tableData,
            file,
            formRef,
            errors,
            setErrors,
            name,
            submittedData,
            tableName,
            setLineItems,
            lineItems,
            isDisabled,
            isMmt
        }: any,
        ref
    ) => {
        const [addNewLineItems, setAddNewLineItems] = useState(1);
        const { toastNotify, workflowProcess } = useContext(AppContext);
        const [data, setData] = useState(tableData);
        const { accountDetails } = useContext(AppContext);
        const [originalData, setOriginalData] = useState<any[]>([]);
        const [isOpen, setIsOpen] = useState(false);
        const [currentObject, setCurrentObject] = useState<any>(null);
        const [currentObjectSchema, setCurrentObjectSchema] = useState<
            Column[] | null
        >(null);
        const [currentRowIndex, setCurrentRowIndex] = useState<any>(null);
        const [currentColIndex, setCurrentColIndex] = useState<any>(null);
        const [showEdited, setShowEdited] = useState<any>([]);
        const [isInitialized, setIsInitialized] = useState(false); // New flag for initialization

        useImperativeHandle(ref, () => ({
            handleSave: () => {
                return handleSave();
            },
        }));

        useEffect(() => {
            console.log("the table data are", tableData);
            console.log("the line items are are are", lineItems);
        }, []);

        console.log("the dta are", data);

        useEffect(() => {
            if (tableName === "Invoice_line_Items") {
                if (!tableData || tableData.length === 0) return;
                setLineItems((prev: any[]) => {
                    const existingMap = new Map(prev.map((li) => [li.line_item_id, li]));

                    // Build merged list from tableData
                    const mergedItems = data.map((td: any) => {
                        const existing = existingMap.get(td.line_item_id);
                        return {
                            ...existing, // keep old fields (like companyCode, glAccount)
                            ...td, // override with tableData fields
                            line_item_id: td.line_item_id,
                            createdAt:
                                td.line_item_time ||
                                existing?.createdAt ||
                                new Date().toISOString(),
                            companyCode: existing?.companyCode ?? 0,
                            costCenter: existing?.costCenter ?? null,
                            taxCode: existing?.taxCode ?? null,
                            glAccount: existing?.glAccount ?? null,
                        };
                    });
                    return mergedItems;
                });
            }
        }, [tableData, data]);

        useEffect(() => {
            // Run only once on mount to initialize data
            if (
                !isInitialized &&
                tableData &&
                JSON.stringify(tableData) !== JSON.stringify(data)
            ) {
                setData(tableData);
                setIsInitialized(true); // Set flag to prevent future runs
            }
        }, []); // Empty dependency array to run only on mount

        type FieldValue = {
            originalValue: any;
            editFieldValue: any;
        };

        type TableRow = Record<string, FieldValue | any>;

        interface FinalTableRow {
            [key: string]:
            | {
                originalValue: any;
                editFieldValue: any;
            }
            | any;
        }

        interface FinalDataObject {
            tables: FinalTableRow[];
        }

        function mergeData(data: any) {
            if (data?.length === 0) return [];

            // Initialize the final tables data structure
            const finalTables: FinalTableRow[] = data[0]?.values?.tables
                ? data[0]?.values?.tables?.map((row: FinalTableRow) =>
                    JSON.parse(
                        JSON.stringify(row, (key, value) =>
                            value === undefined ? null : value
                        )
                    )
                )
                : [];

            // Function to recursively merge field values in tables
            function mergeFieldValues(
                finalRow: FinalTableRow,
                currentRow: FinalTableRow
            ) {
                for (const key in currentRow) {
                    if (Object.prototype.hasOwnProperty.call(currentRow, key)) {
                        const currentField = currentRow[key];

                        // Check if the current field is an object with 'originalValue' and 'editFieldValue'
                        if (
                            currentField &&
                            typeof currentField === "object" &&
                            currentField?.hasOwnProperty("originalValue") &&
                            currentField?.hasOwnProperty("editFieldValue")
                        ) {
                            if (!finalRow[key]) {
                                finalRow[key] = {
                                    originalValue: currentField?.originalValue,
                                    editFieldValue: currentField?.editFieldValue,
                                };
                            } else {
                                finalRow[key] = {
                                    originalValue:
                                        finalRow[key].originalValue ?? currentField.originalValue,
                                    editFieldValue:
                                        currentField.editFieldValue !== null
                                            ? currentField.editFieldValue
                                            : finalRow[key].editFieldValue ?? null,
                                };
                            }
                        } else if (
                            currentField &&
                            typeof currentField === "object" &&
                            !Array.isArray(currentField)
                        ) {
                            // Handle nested objects
                            if (!finalRow[key]) finalRow[key] = {};
                            mergeFieldValues(finalRow[key], currentRow[key]);
                        } else {
                            // For primitive types or arrays, directly assign the value
                            finalRow[key] =
                                currentField !== undefined ? currentField : finalRow[key];
                        }
                    }
                }
            }

            // Iterate from the first element to the last to ensure latest edits are considered
            for (let i = 0; i < data.length; i++) {
                const currentData = data[i];

                // Merge tables
                for (
                    let rowIndex = 0;
                    rowIndex < currentData.values.tables.length;
                    rowIndex++
                ) {
                    const currentRow = currentData?.values?.tables[rowIndex];

                    if (!finalTables[rowIndex]) {
                        finalTables[rowIndex] = {};
                    }

                    mergeFieldValues(finalTables[rowIndex], currentRow);
                }
            }

            // Ensure originalValue is taken from the first occurrence
            finalTables.forEach((row, index: number) => {
                // Ensure that the index is within bounds
                if (index < data.length) {
                    const sourceRow = data[index].values?.tables;

                    if (sourceRow) {
                        for (const key in row) {
                            if (Object.prototype.hasOwnProperty.call(row, key)) {
                                const field = row[key];
                                if (
                                    field &&
                                    typeof field === "object" &&
                                    field.hasOwnProperty("originalValue")
                                ) {
                                    // Find the source row that corresponds to the current row
                                    const sourceRowEntry = sourceRow.find(
                                        (tableRow: any) =>
                                            tableRow[key] &&
                                            tableRow[key].hasOwnProperty("originalValue")
                                    );

                                    // Assign the original value from the source row, or null if not found
                                    field.originalValue =
                                        sourceRowEntry?.[key]?.originalValue ?? null;
                                }
                            }
                        }
                    }
                }
            });
            return finalTables;
        }

        useEffect(() => {
            if (Array.isArray(submittedData)) {
                const mergedData = mergeData(submittedData);
                setShowEdited(mergedData);
            }
        }, []);

        const validateRow = (
            row: any,
            rowIndex: number,
            newErrors: any,
            skipRequired: boolean = false // Add parameter to skip required validation for new rows
        ): boolean => {
            let isValid = true;
            const rowErrors: { [key: string]: string } = {};
            const objectErrors: { [key: string]: string } = {};

            schema.columns.forEach((column: any, colIndex: number) => {
                const key = column.name;
                const value = row[key];

                if (!skipRequired) {
                    if (
                        (column.required &&
                            (value === "" ||
                                value === null ||
                                value === undefined ||
                                value === "N/A")) ||
                        value === "N/A"
                    ) {
                        rowErrors[key] = "This field is required";
                        isValid = false;
                    }
                }

                switch (column.type && value) {
                    case "number":
                        if (
                            typeof value !== "number" ||
                            value < (column.min || 0) ||
                            value > (column.max || Infinity)
                        ) {
                            rowErrors[key] = `Value should be a number between ${column.min || 0
                                } and ${column.max || Infinity}`;
                            isValid = false;
                        }
                        break;
                    case "string":
                        if (
                            typeof value !== "string" ||
                            value.length < (column.minLength || 0) ||
                            value.length > (column.maxLength || Infinity)
                        ) {
                            rowErrors[key] = `Value should be a string with length between ${column.minLength || 0
                                } and ${column.maxLength || Infinity}`;
                            isValid = false;
                        }
                        break;
                    case "date":
                        const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
                        if (!dateRegex.test(value)) {
                            rowErrors[key] = `Date should be in the format ${column.format}`;
                            isValid = false;
                        }
                        break;
                    case "email":
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(value)) {
                            rowErrors[key] = "Invalid email format";
                            isValid = false;
                        }
                        break;
                    case "boolean":
                        if (typeof value !== "boolean") {
                            rowErrors[key] = "Value should be true or false";
                            isValid = false;
                        }
                        break;
                    case "object":
                        if (typeof value !== "object" || Array.isArray(value)) {
                            rowErrors[key] = "Invalid object format";
                            isValid = false;
                        } else {
                            (column.fields || []).forEach((field: any) => {
                                const fieldValue = value[field.name];
                                if (
                                    !skipRequired &&
                                    field.required &&
                                    (fieldValue === "" ||
                                        fieldValue === null ||
                                        fieldValue === undefined ||
                                        fieldValue === "N/A")
                                ) {
                                    objectErrors[
                                        `${key}.${field.name}`
                                    ] = `Field "${field.name}" is required`;
                                    isValid = false;
                                } else if (
                                    field.type === "number" &&
                                    (typeof fieldValue !== "number" ||
                                        fieldValue < (field.min || 0) ||
                                        fieldValue > (field.max || Infinity))
                                ) {
                                    objectErrors[`${key}.${field.name}`] = `Value of "${field.name
                                        }" should be a number between ${field.min || 0} and ${field.max || Infinity
                                        }`;
                                    isValid = false;
                                } else if (
                                    field.type === "string" &&
                                    (typeof fieldValue !== "string" ||
                                        fieldValue.length < (field.minLength || 0) ||
                                        fieldValue.length > (field.maxLength || Infinity))
                                ) {
                                    objectErrors[`${key}.${field.name}`] = `Value of "${field.name
                                        }" should be a string with length between ${field.minLength || 0
                                        } and ${field.maxLength || Infinity}`;
                                    isValid = false;
                                }
                            });
                        }
                        break;
                    default:
                        break;
                }
            });
            if (!isValid) {
                newErrors[rowIndex] = { ...rowErrors, ...objectErrors };
            }
            return isValid;
        };

        const handleInputChange = (
            value: any,
            rowIndex: number,
            colIndex: number
        ) => {
            const newData = [...data];
            const column = schema.columns[colIndex];
            const key = column.name;

            // Update the edited value only
            newData[rowIndex][key] = value;

            setData(newData);
        };

        const handleAddRow = (count = 1) => {
            const createRow = () => {
                const newRow = schema.columns.reduce((acc: any, column: any) => {
                    switch (column.type) {
                        case "object":
                            acc[column.name] = column.fields
                                ? column.fields.reduce((nestedAcc: any, field: any) => {
                                    switch (field.type) {
                                        case "number":
                                            nestedAcc[field.name] = 0;
                                            break;
                                        case "boolean":
                                            nestedAcc[field.name] = false;
                                            break;
                                        case "string":
                                        case "date":
                                        case "email":
                                            nestedAcc[field.name] = field.required ? "N/A" : "";
                                            break;
                                        default:
                                            nestedAcc[field.name] = "";
                                    }
                                    return nestedAcc;
                                }, {})
                                : {};
                            break;

                        case "number":
                            acc[column.name] = 0;
                            break;

                        case "boolean":
                            acc[column.name] = false;
                            break;

                        case "string":
                        case "date":
                        case "email":
                            acc[column.name] = column.required ? "N/A" : "";
                            break;

                        default:
                            acc[column.name] = "";
                    }
                    return acc;
                }, {});

                return {
                    ...newRow,
                    line_item_id: uuidv4(),
                    line_item_time: new Date().toISOString(),
                };
            };

            // generate an array with "count" rows
            const rowsToAdd = Array.from({ length: count }, () => createRow());

            // update lineItems
            setLineItems((prev: any) => [...prev, ...rowsToAdd]);

            // update data
            setData((prev: any) => [...prev, ...rowsToAdd]);
        };

        const handleDeleteRow = (rowIndex: number) => {
            const newData = data.filter(
                (_: any, index: number) => index !== rowIndex
            );
            setData(newData);
        };

        function isEmpty(obj: any): boolean {
            if (obj === null || typeof obj !== "object") {
                return false;
            }
            if (Object.keys(obj).length === 0) {
                return true;
            }
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    if (isEmpty(obj[key])) {
                        continue;
                    } else {
                        return false;
                    }
                }
            }
            return true;
        }

        useEffect(() => {
            const newErrors: any = {};
            const combinedValue = Array.isArray(data)
                ? data.map((row: any, rowIndex: number) => {
                    // Skip required field validation for the last row (newly added)
                    const skipRequired = rowIndex === data.length - 1;
                    if (!validateRow(row, rowIndex, newErrors, skipRequired)) {
                        return null;
                    }
                    return schema.columns.reduce((acc: any, column: any) => {
                        const key = column.name;
                        const value = row[key];
                        const originalValue =
                            originalData[rowIndex]?.[key]?.originalValue ??
                            (column.type === "object"
                                ? {}
                                : column.type === "array"
                                    ? []
                                    : column.type === "string"
                                        ? ""
                                        : column.type === "number"
                                            ? ""
                                            : column.type === "boolean"
                                                ? false
                                                : column.type === "date"
                                                    ? ""
                                                    : "");

                        const formatField = (original: any, edited: any) => {
                            if (original === edited) {
                                return {
                                    originalValue: original,
                                    editFieldValue: null,
                                };
                            } else {
                                return {
                                    originalValue: original,
                                    editFieldValue: edited,
                                };
                            }
                        };

                        if (Array.isArray(value)) {
                            // Handle arrays
                            acc[key] = formatField(originalValue, value);
                        } else if (typeof value === "object" && !Array.isArray(value)) {
                            // Handle nested objects
                            acc[key] = Object.keys(value).reduce((objAcc, nestedKey) => {
                                const originalField =
                                    originalData[rowIndex]?.[key]?.[nestedKey]?.originalValue ??
                                    value[nestedKey];
                                objAcc[nestedKey] = formatField(
                                    originalField,
                                    value[nestedKey]
                                );
                                return objAcc;
                            }, {} as any);
                        } else {
                            // Handle primitive values
                            acc[key] = formatField(originalValue, value);
                        }
                        return acc;
                    }, {} as any);
                })
                : [];
            // setErrors(newErrors);
        }, [data]);

        const handleSave = () => {
            const newErrors: any = {};
            const combinedData = data.map((row: any, rowIndex: number) => {
                if (!validateRow(row, rowIndex, newErrors)) {
                    return null;
                }
                return schema.columns.reduce((acc: any, column: any) => {
                    const key = column.name;
                    const value = row[key];
                    const originalValue =
                        originalData[rowIndex]?.[key]?.originalValue ??
                        (column.type === "object"
                            ? {}
                            : column.type === "array"
                                ? []
                                : column.type === "string"
                                    ? ""
                                    : column.type === "number"
                                        ? ""
                                        : column.type === "boolean"
                                            ? false
                                            : column.type === "date"
                                                ? ""
                                                : "");

                    const formatField = (original: any, edited: any) => {
                        if (original === edited) {
                            return {
                                originalValue: original,
                                editFieldValue: null,
                            };
                        } else {
                            return {
                                originalValue: original,
                                editFieldValue: edited,
                            };
                        }
                    };

                    if (Array.isArray(value)) {
                        // Handle arrays
                        acc[key] = formatField(originalValue, value);
                    } else if (typeof value === "object" && !Array.isArray(value)) {
                        // Handle nested objects
                        acc[key] = Object.keys(value).reduce((objAcc, nestedKey) => {
                            const originalField =
                                originalData[rowIndex]?.[key]?.[nestedKey]?.originalValue ??
                                value[nestedKey];
                            objAcc[nestedKey] = formatField(originalField, value[nestedKey]);
                            return objAcc;
                        }, {} as any);
                    } else {
                        // Handle primitive values
                        acc[key] = formatField(originalValue, value);
                    }
                    return acc;
                }, {} as any);
            });

            // Filter out rows that failed validation
            const validCombinedData = combinedData.filter((row: any) => row !== null);

            // Set the new errors state
            setErrors(newErrors);

            if (Object.keys(newErrors).length === 0 || isEmpty(newErrors)) {
                onSubmit(validCombinedData);
                return true;
            }
            // Optionally reset isInitialized to allow re-initialization if tableData changes
            // setIsInitialized(false);

            if (Object.keys(newErrors).length !== 0 && !isEmpty(newErrors)) {
                // Find the first non-empty error
                let firstError: any = Object.values(newErrors).find((err) => {
                    if (err == null) return false; // skip null/undefined
                    if (typeof err === "object") return Object.keys(err).length > 0; // skip empty objects
                    if (typeof err === "string" && err.trim() === "") return false; // skip empty string
                    return true;
                });

                // Normalize
                if (typeof firstError === "object") {
                    try {
                        // if it's { field: "msg" } → take "field: msg"
                        const entries = Object.entries(firstError);
                        firstError =
                            entries.length > 0
                                ? `${entries[0][0]}: ${entries[0][1]}`
                                : JSON.stringify(firstError);
                    } catch {
                        firstError = String(firstError);
                    }
                } else if (!firstError) {
                    firstError = "Unknown validation error";
                } else {
                    firstError = String(firstError);
                }

                toastNotify({
                    title: "Validation Error",
                    description: firstError,
                    message: firstError,
                    type: "info",
                });
                return false;
            }
            return true;
        };

        useEffect(() => {
            if (tableData.length) {
                // Initialize `originalData` with original values only
                setOriginalData(
                    tableData.map((row: any) =>
                        Object.keys(row).reduce((acc: any, key: string) => {
                            const cell = row[key];
                            if (typeof cell === "object" && !Array.isArray(cell)) {
                                // Handle nested objects
                                acc[key] = Object.keys(cell || {}).reduce(
                                    (objAcc, nestedKey) => {
                                        objAcc[nestedKey] = {
                                            originalValue: cell[nestedKey],
                                            editFieldValue: cell[nestedKey],
                                        };
                                        return objAcc;
                                    },
                                    {} as any
                                );
                            } else {
                                // Handle primitive values
                                acc[key] = {
                                    originalValue: cell,
                                    editFieldValue: cell,
                                };
                            }
                            return acc;
                        }, {} as any)
                    )
                );
                // setData(tableData); // Removed to avoid duplicate initialization
            }
        }, []);

        const handleOpenModal = (rowIndex: number, colIndex: number) => {
            const column = schema.columns[colIndex];
            const key = column.name;
            const row = data[rowIndex];

            setCurrentObject(row[key]);
            setCurrentObjectSchema(column.fields);
            setCurrentRowIndex(rowIndex);
            setCurrentColIndex(colIndex);
            setIsOpen(true);
        };

        const handleCloseModal = () => {
            setIsOpen(false);
            setCurrentObject(null);
            setCurrentObjectSchema(null);
            setCurrentRowIndex(null);
            setCurrentColIndex(null);
        };

        const handleModalInputChange = (value: any, fieldName: string) => {
            if (
                currentObject &&
                currentObjectSchema &&
                currentRowIndex !== null &&
                currentColIndex !== null
            ) {
                const newObject = { ...currentObject, [fieldName]: value };
                // Validate the updated object field
                const field = currentObjectSchema.find((f) => f.name === fieldName);
                if (field) {
                    let errorMessage = "";
                    if (
                        field.required &&
                        (value === "" || value === null || value === undefined)
                    ) {
                        errorMessage = `Field "${fieldName}" is required`;
                    } else if (
                        field.type === "number" &&
                        (typeof value !== "number" ||
                            value < (field.min || 0) ||
                            value > (field.max || Infinity))
                    ) {
                        errorMessage = `Value of "${fieldName}" should be a number between ${field.min || 0
                            } and ${field.max || Infinity}`;
                    } else if (
                        field.type === "string" &&
                        (typeof value !== "string" ||
                            value.length < (field.minLength || 0) ||
                            value.length > (field.maxLength || Infinity))
                    ) {
                        errorMessage = `Value of "${fieldName}" should be a string with length between ${field.minLength || 0
                            } and ${field.maxLength || Infinity}`;
                    }

                    // Update the errors state
                    setErrors((prevErrors: any) => ({
                        ...prevErrors,
                        [currentRowIndex!]: {
                            ...(prevErrors[currentRowIndex!] || {}),
                            [`${currentColIndex!}-${fieldName}`]: errorMessage,
                        },
                    }));
                }

                // Update the current object with the new value
                setCurrentObject(newObject);
            }
        };

        const getObjectData = (rowIndex: number, columnIndex: number): any => {
            const row = showEdited[rowIndex]; // Get the row object based on rowIndex
            if (!row) return null;

            // Get all the keys of the row
            const keys = Object.keys(row);

            // Ensure columnIndex is within the range of keys
            if (columnIndex < 0 || columnIndex >= keys.length) return null;

            // Get the key based on columnIndex
            const key = keys[columnIndex];

            return key;
        };

        return (
            <>
                <Box
                    overflow="auto"
                    className="customScrollBar"
                // maxH={"65vh"}
                // maxW={{ base: "85vw", md: "90vw", lg: file ? "48vw" : "70vw" }}
                >
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                {schema.columns.map(
                                    (column: any, colIndex: number) =>
                                        column.visible !== false && (
                                            <Th key={colIndex}>
                                                {column.name?.split("_")?.join(" ")}
                                            </Th>
                                        )
                                )}
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <TableLoader loader={false} show={data.length}>
                            <Tbody>
                                {data?.map((row: any, rowIndex: number) => {
                                    return (
                                        <Tr key={rowIndex}>
                                            {schema.columns.map((column: any, colIndex: number) => {
                                                // Check if the column is visible
                                                if (column.visible === false) return null;
                                                const key = column.name; // Assuming the column's key matches the column name
                                                const cell = row[key];
                                                const isEdited =
                                                    showEdited &&
                                                    showEdited[rowIndex] &&
                                                    showEdited[rowIndex][key]?.originalValue !== cell;

                                                return (
                                                    <Td key={colIndex} minW={"8rem"}>
                                                        <FormControl
                                                            isInvalid={
                                                                !!(errors[rowIndex] && errors[rowIndex][key])
                                                            }
                                                            variant={
                                                                typeof cell === "boolean"
                                                                    ? "default"
                                                                    : typeof cell === "object"
                                                                        ? "default"
                                                                        : "floating"
                                                            }
                                                        >
                                                            <Flex
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                flexDirection="column"
                                                            >
                                                                {typeof cell === "object" &&
                                                                    !Array.isArray(cell) ? (
                                                                    <Button
                                                                        colorScheme="teal"
                                                                        onClick={() =>
                                                                            handleOpenModal(rowIndex, colIndex)
                                                                        }
                                                                        size="sm"
                                                                    >
                                                                        <Text
                                                                            ml={1}
                                                                            color={isEdited ? "red.200" : "white"}
                                                                        >
                                                                            {isEdited ? "(Edited)" : "Edit"}
                                                                        </Text>
                                                                    </Button>
                                                                ) : typeof cell === "boolean" ? (
                                                                    <Checkbox
                                                                        isChecked={cell}
                                                                        size={"lg"}
                                                                        isReadOnly={!column.isEditable}
                                                                        onChange={(e) =>
                                                                            handleInputChange(
                                                                                e.target.checked,
                                                                                rowIndex,
                                                                                colIndex
                                                                            )
                                                                        }
                                                                        colorScheme="teal"
                                                                    >
                                                                        <Text
                                                                            color={isEdited ? "red.500" : "gray.500"}
                                                                            display="flex"
                                                                            fontSize={"sm"}
                                                                        >
                                                                            {isEdited ? "Edited" : ""}
                                                                        </Text>
                                                                    </Checkbox>
                                                                ) : (
                                                                    <Flex>
                                                                        <Input
                                                                            w={"fit-content"}
                                                                            placeholder=""
                                                                            borderWidth={2}
                                                                            value={column.type === "date" ? dateFormatter(cell, 'input') : cell ?? cell}
                                                                            isReadOnly={!column.isEditable}
                                                                            onChange={(e) => {
                                                                                handleInputChange(
                                                                                    typeof cell === "number"
                                                                                        ? parseFloat(e.target.value)
                                                                                        : e.target.value,
                                                                                    rowIndex,
                                                                                    colIndex
                                                                                )
                                                                            }
                                                                            }
                                                                            type={
                                                                                column.type === "date" ? "date" : "text"
                                                                            }
                                                                            borderColor={
                                                                                isEdited ? "red.500" : "gray.200"
                                                                            }
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Flex>
                                                                )}
                                                                {typeof cell !== "object" &&
                                                                    typeof cell !== "boolean" && (
                                                                        <FormLabel
                                                                            position="absolute"
                                                                            top={cell ? "-1rem" : "50%"}
                                                                            left="0.75rem"
                                                                            transform={
                                                                                cell
                                                                                    ? "scale(0.85) translateY(-1.5rem)"
                                                                                    : "scale(1)"
                                                                            }
                                                                            transformOrigin="top left"
                                                                            transition="0.2s ease all"
                                                                            bg="white"
                                                                            px={1}
                                                                            pointerEvents="none"
                                                                            color={isEdited ? "red.500" : "gray.500"}
                                                                        >
                                                                            {isEdited ? "Edited" : ""}
                                                                        </FormLabel>
                                                                    )}
                                                                <FormErrorMessage fontSize={"xs"}>
                                                                    {errors[rowIndex] && errors[rowIndex][key]}
                                                                </FormErrorMessage>
                                                            </Flex>
                                                        </FormControl>
                                                    </Td>
                                                );
                                            })}
                                            <Td>
                                                <Button
                                                    colorScheme="red"
                                                    onClick={() => handleDeleteRow(rowIndex)}
                                                    size="sm"
                                                    isDisabled={isDisabled}
                                                    display={data?.length === 1 ? "none" : undefined}
                                                >
                                                    Delete
                                                </Button>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </TableLoader>
                    </Table>
                </Box>
                {true && (
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mt={4}
                        p={3}
                        borderRadius="md"
                        bg="gray.50"
                        boxShadow="sm"
                        display={isMmt ? 'none' : undefined}
                    >
                        <Flex align="center" w="50%" py={2} gap={6}>
                            <Box></Box>
                            <Text display={workflowProcess?.processType === "Po" ? 'none' : undefined}
                                fontSize="md" fontWeight="600" color="gray.700">
                                Total Amount:{" "}
                                <Text
                                    as="span"
                                    ml={2}
                                    fontSize="xl"
                                    fontWeight="700"
                                    color="teal.600"
                                >
                                    {sumLineAmounts(lineItems)}
                                </Text>
                            </Text>

                            <Text
                                fontSize="md"
                                fontWeight="500"
                                color="gray.600"
                                bg="gray.100"
                                px={3}
                                py={1}
                                borderRadius="md"
                                display={workflowProcess?.processType === "Po" ? 'none' : undefined}

                            >
                                Line Items:{" "}
                                <Text as="span" fontWeight="700" color="gray.800">
                                    {lineItems?.length ?? 0}
                                </Text>
                            </Text>
                        </Flex>

                        <Flex
                            display={
                                name === "Pending Approval" &&
                                    accountDetails.level === "level-1" &&
                                    accountDetails.level !== "admin"
                                    ? undefined
                                    : getFiUser(workflowProcess, accountDetails?.level)
                                        ? undefined
                                        : "none"
                            }
                            gap={4}
                        >
                            <NumberInput
                                value={addNewLineItems}
                                min={1}
                                max={50}
                                width="100px"
                                onChange={(value) => setAddNewLineItems(Number(value))}
                            >
                                <NumberInputField placeholder="Count" />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>

                            <Button
                                colorScheme="teal"
                                variant="outline"
                                onClick={() => handleAddRow(addNewLineItems)}
                                display={
                                    name === "Pending Approval" &&
                                        accountDetails.level === "level-1" ||
                                        accountDetails.level === "admin"
                                        ? undefined
                                        : getFiUser(
                                            workflowProcess?.processName,
                                            accountDetails?.level
                                        )
                                            ? undefined
                                            : "none"
                                }
                            >
                                Add Row
                            </Button>

                            <Button
                                colorScheme="green"
                                onClick={handleSave}
                                px={6}
                                display={
                                    name === "Pending Approval" &&
                                        accountDetails.level === "level-1" &&
                                        accountDetails.level !== "admin"
                                        ? undefined
                                        : getFiUser(
                                            workflowProcess?.processName,
                                            accountDetails?.level
                                        )
                                            ? "none"
                                            : undefined
                                }
                            >
                                Save
                            </Button>
                        </Flex>
                    </Flex>
                )}
                <Modal isOpen={isOpen} onClose={handleCloseModal} size="5xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader
                            fontSize={{ base: "md", lg: "xl" }}
                            color="teal.300"
                            fontWeight="700"
                            p=".6rem .6rem"
                            ml="1rem"
                        >
                            Edit
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Box overflow="auto" className="customScrollBar">
                                {currentObject && currentObjectSchema && (
                                    <Table variant="simple">
                                        <Thead>
                                            <Tr>
                                                {currentObjectSchema?.map((field: any) => (
                                                    <Th key={field.name}>{field.name}</Th>
                                                ))}
                                            </Tr>
                                        </Thead>
                                        <TableLoader
                                            loader={false}
                                            show={currentObjectSchema?.length}
                                        >
                                            <Tbody>
                                                <Tr>
                                                    {currentObjectSchema.map(
                                                        (field: any, columnIndex: number) => {
                                                            return (
                                                                <Td key={field.name}>
                                                                    <FormControl
                                                                        isInvalid={
                                                                            !!(
                                                                                errors[currentRowIndex!] &&
                                                                                errors[currentRowIndex!][
                                                                                `${currentColIndex!}-${field.name}`
                                                                                ]
                                                                            )
                                                                        }
                                                                        variant={"floating"}
                                                                    >
                                                                        <Input
                                                                            value={currentObject[field.name]}
                                                                            onChange={(
                                                                                e: ChangeEvent<HTMLInputElement>
                                                                            ) =>
                                                                                handleModalInputChange(
                                                                                    field.type === "number"
                                                                                        ? parseFloat(e.target.value)
                                                                                        : e.target.value,
                                                                                    field.name
                                                                                )
                                                                            }
                                                                            type={
                                                                                field.type === "number"
                                                                                    ? "number"
                                                                                    : "text"
                                                                            }
                                                                            placeholder=""
                                                                            borderWidth={2}
                                                                        />
                                                                        <FormErrorMessage>
                                                                            {errors[currentRowIndex!] &&
                                                                                errors[currentRowIndex!][
                                                                                `${currentColIndex!}-${field.name}`
                                                                                ]}
                                                                        </FormErrorMessage>
                                                                        <FormLabel
                                                                            color={
                                                                                Array.isArray(showEdited) &&
                                                                                    showEdited.length > 0 &&
                                                                                    currentRowIndex >= 0 &&
                                                                                    currentRowIndex < showEdited.length &&
                                                                                    showEdited[currentRowIndex] &&
                                                                                    typeof showEdited[currentRowIndex] ===
                                                                                    "object" &&
                                                                                    currentObject &&
                                                                                    currentObject[field.name] !==
                                                                                    showEdited[currentRowIndex][
                                                                                    field.name
                                                                                    ]
                                                                                    ? "red.500"
                                                                                    : "gray.700"
                                                                            }
                                                                        >
                                                                            {Array.isArray(showEdited) &&
                                                                                showEdited.length > 0 &&
                                                                                currentRowIndex >= 0 &&
                                                                                currentRowIndex < showEdited.length &&
                                                                                currentObject &&
                                                                                currentObject[field.name] !==
                                                                                (showEdited[currentRowIndex] &&
                                                                                    typeof showEdited[currentRowIndex] ===
                                                                                    "object" &&
                                                                                    getObjectData(
                                                                                        currentRowIndex,
                                                                                        currentColIndex
                                                                                    ) &&
                                                                                    showEdited[currentRowIndex][
                                                                                    getObjectData(
                                                                                        currentRowIndex,
                                                                                        currentColIndex
                                                                                    )
                                                                                    ] &&
                                                                                    showEdited[currentRowIndex][
                                                                                        getObjectData(
                                                                                            currentRowIndex,
                                                                                            currentColIndex
                                                                                        )
                                                                                    ][field.name]?.originalValue)
                                                                                ? "Edited"
                                                                                : ""}
                                                                        </FormLabel>
                                                                    </FormControl>
                                                                </Td>
                                                            );
                                                        }
                                                    )}
                                                </Tr>
                                            </Tbody>
                                        </TableLoader>
                                    </Table>
                                )}
                            </Box>
                        </ModalBody>
                        {name === "Pending Approval" && (
                            <ModalFooter>
                                <Button
                                    colorScheme="green"
                                    onClick={() => {
                                        if (
                                            currentObject &&
                                            currentRowIndex !== null &&
                                            currentColIndex !== null
                                        ) {
                                            const newData = [...data];
                                            newData[currentRowIndex][
                                                schema.columns[currentColIndex].name
                                            ] = currentObject;
                                            setData(newData);
                                            handleCloseModal();
                                        }
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </ModalFooter>
                        )}
                    </ModalContent>
                </Modal>
            </>
        );
    }
);

export default MtDynamicTableLabel;